import React, {Fragment} from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {UserAvatar} from 'app/components/general/avatars';
import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {CopyClipboardInputField} from 'app/components/general/CopyClipboardInputField';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import MetaContentTypeName from 'app/general/structures/MetaContentTypeName';
import {useKnowledgeBase} from 'app/kbs/api';
import {DomainConfiguration} from 'app/kbs/components/DomainConfiguration';
import {DataSharingEnabledGate} from 'app/openai/components/DataSharingEnabledGate';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {DEFAULT_THEME} from './EditKBTheme';
import {IndentedFormGroup} from './IndentedFormGroup';
import {ViewThemeMeta} from './ViewThemeMeta';

type Props = WithRouterProps<{hashid: string}>;

export const ViewKBMetadata = withRouter(({params}: Props) => {
  const {data: kb} = useKnowledgeBase({kb_id: params.hashid});

  const contactUserElement =
    kb?.questions && kb?.contact_user != null ? (
      <UserAvatar className="ml-1" user={kb.contact_user} small={true} />
    ) : null;

  return (
    <DocHeaderStructure
      warningContent={
        !kb?.enabled ? (
          <div className="alert alert-warning d-flex justify-content-between align-items-center">
            <div>{gt('This Knowledge Base is currently disabled.')}</div>
            <button type="button" className="btn btn-sm btn-warning">
              {gt('Edit')}
            </button>
          </div>
        ) : null
      }
      advancedContent={
        <MetaContentTypeName
          contentType="knowledgebase"
          statusContent={
            kb && (
              <DocumentMetadataStatusButtons
                canEdit={true}
                context={'view'}
                contentType="knowledgebase"
                document={kb}
              />
            )
          }
        />
      }
      titleContent={
        <h1>
          {getObjectName(kb?.name, 'knowledgebase')}
          <ClickToEditButton />
        </h1>
      }
      extraContent={
        <div className="mt-3">
          <IndentedFormGroup title={gt('Theme')}>
            <ViewThemeMeta
              isSelected={false}
              theme={kb?.theme ?? DEFAULT_THEME}
              kbName={kb?.name ?? 'Default Name'}
            />
          </IndentedFormGroup>
          <IndentedFormGroup title={gt('Control Access')}>
            <ul>
              <li>
                {kb?.private
                  ? gt(
                      'Private Knowledge Base, only logged in team members may view (if they have the necessary permissions)'
                    )
                  : gt(
                      'Public Knowledge Base. Any member of the public can view without logging in'
                    )}
              </li>
              {kb?.redirection_url != null && kb?.redirection_url !== '' ? (
                <li>
                  {gt(
                    'Redirect anyone clicking the logo to "%s".',
                    kb?.redirection_url
                  )}
                </li>
              ) : null}
              <DataSharingEnabledGate
                sharingEnabledElement={
                  kb?.sweetai_chat_enabled ? (
                    <li>
                      {gt(
                        'SweetAI Chat is ON. Our chat will assist visitors by finding relevant information from this Knowledge Base.'
                      )}
                    </li>
                  ) : (
                    <li>
                      {gt(
                        'SweetAI Chat is OFF. Visitors won’t get automated help from this Knowledge Base.'
                      )}
                    </li>
                  )
                }
                sharingDisabledElement={
                  <li>
                    {gt(
                      'Enable data sharing in your account to let SweetAI assist visitors with information from this Knowledge Base.'
                    )}
                  </li>
                }
              />
            </ul>
          </IndentedFormGroup>
          <IndentedFormGroup title={gt('Feedback')}>
            <ul>
              <li>
                {kb?.feedback
                  ? gt(
                      'Asks team members for feedback at the end of each article'
                    )
                  : gt(
                      'Does not ask team members for feedback at the end of each article'
                    )}
              </li>
              {!kb?.private ? (
                <Fragment>
                  <li>
                    {kb?.feedback_external
                      ? gt(
                          'Asks members of the public for feedback at the end of each article'
                        )
                      : gt(
                          'Does not ask members of the public for feedback at the end of each article'
                        )}
                  </li>
                  <li>
                    <span className="mr-2">
                      {kb?.questions
                        ? gt('Showing a contact form')
                        : gt('Not showing a contact form')}
                    </span>
                    {contactUserElement != null ? (
                      <span className="small text-secondary font-italic d-inline-flex align-items-center">
                        {gt(
                          'The messages will be sent to %s',
                          contactUserElement
                        )}
                      </span>
                    ) : null}
                  </li>
                </Fragment>
              ) : null}
            </ul>
          </IndentedFormGroup>
          <IndentedFormGroup title={gt('Text Blocks')}>
            <p>
              {gt(
                "Customize your knowledge base by editing text blocks to match your brand's voice and information needs."
              )}
            </p>

            <ul>
              <li>
                <b>{gt('Search Placeholder')}</b>{' '}
                {kb?.blocks.search_placeholder?.content ??
                  gt('Type your question here...')}
              </li>
              <li>
                <b>{gt('Breadcrumb Home Text')}</b>{' '}
                {kb?.blocks.breadcrumb_home_text?.content ?? gt('Help')}
              </li>
              <li>
                <b>{gt('Index Page Category Heading')}</b>{' '}
                {kb?.blocks.index_category_heading?.content ??
                  gt('Common Help Topics')}
              </li>
              <li>
                <b>{gt('Sidebar Category Heading')}</b>{' '}
                {kb?.blocks.category_heading?.content ?? gt('Main Topics')}
              </li>
              <li>
                <b>{gt('Contact Form Heading')}</b>{' '}
                {kb?.blocks.contact_form_heading?.content ??
                  gt('Looking for something else?')}
              </li>
              <li>
                <b>{gt('Contact Form Subject Label')}</b>{' '}
                {kb?.blocks.contact_form_subject_label?.content ??
                  gt('Subject')}
              </li>
              <li>
                <b>{gt('Contact Form Message Label')}</b>{' '}
                {kb?.blocks.contact_form_body_label?.content ??
                  gt('What is your question or feedback? Ask us anything :)')}
              </li>
              <li>
                <b>{gt('Contact Page Heading')}</b>{' '}
                {kb?.blocks.contact_page_heading?.content ??
                  gt("We're here to help")}
              </li>
            </ul>
          </IndentedFormGroup>

          <IndentedFormGroup title={gt('Knowledge Base URL')}>
            <div className="d-flex flex-wrap align-items-end">
              {kb && (
                <CopyClipboardInputField
                  className="pl-0 col-12 col-sm my-0 flex-grow-1 pr-sm-2"
                  id={'kb-' + kb?.id}
                  disabled={kb?.enabled === false}
                  label={kb?.private ? gt('Private URL') : gt('Public URL')}
                  value={kb?.html_url}
                />
              )}
              <a
                href={kb?.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-2 mt-sm-0 btn btn-light ${
                  !kb?.enabled ? 'disabled' : ''
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                {gt('Visit Knowledge Base')}
              </a>
            </div>
            {kb?.acm_hashid ? (
              <DomainConfiguration acm_hashid={kb.acm_hashid} kb={kb} />
            ) : (
              <p className="mt-3">
                {gt(
                  'Did you know? You can brand your knowledge base with a custom domain like help.yourcompany.com.'
                )}
              </p>
            )}
          </IndentedFormGroup>
        </div>
      }
    />
  );
});
